/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { WidgetContext } from './widget-context-base';
import { iamLogin, iamUtils } from '../main';
import { WidgetPage } from '../pages/widget-page-base';
import { WidgetElements } from '../elements/widget-elements-base';
import { iam } from '../iam';

export class ChangePasswordWidgetContext extends WidgetContext {
  // The tooltip, the valid indicator icons and the scrollable right pane all live outside the widget, so they survive
  // the widget moving to another screen or being re-rendered for a locale change. Track what we set up on them so
  // deinitialize() can undo it; otherwise the intervals keep running forever against detached fields, and each visit
  // to this screen adds another set of intervals, scroll listeners and (duplicate id) indicator icons.
  private intervals: ReturnType<typeof setInterval>[] = [];
  private containerScrollListeners: (() => void)[] = [];
  private statusIcons: SVGElement[] = [];

  constructor(elements: WidgetElements, private contextName: string) {
    super(elements);
  }

  public override initialize() {
    // updatepassword.html page's main context
    const expired = this.isPasswordExpiredWidget();
    if (expired) {
      if (iam.settings.debug) console.log('initializing expired password widget');
      document.getElementById('sub-header').textContent = this.elements.getFormHead().textContent; // "your password has expired"
    } else {
      if (iam.settings.debug) console.log('initializing reset password widget');
    }
    document.getElementById('dont-have-a-mvision').style.display = 'none';
    iamLogin.showWidget();
    iamLogin.page.showCommonFields(['post-okta']);

    const container = document.getElementById('right-pane-contents');
    const tooltip = document.getElementById('infotooltip');
    const positionTooltip = (field: HTMLInputElement) => {
      // to the left of the field
      const rect = document.body.getBoundingClientRect();
      const pos = iamUtils.getRelativePosition(field, document.body);
      tooltip.style.right = (rect.width - pos.left + 15) + 'px';
      tooltip.style.top = (pos.top - 1) + 'px';
    };

    let focusedField: HTMLInputElement = null;
    const setFocusAndBlurHandlers = (field: HTMLInputElement, contents: HTMLElement) => {
      let focused = false;
      const onFocus = () => {
        focused = true;
        // since there's potentially a delay while transitioning, make sure focus is still present
        // before showing the tooltip. if focus was quickly lost, then the tooltip shouldn't be shown
        this.runWhenNotContextTransitioning(() => {
          if (focused) {
            focusedField = field;
            contents.style.display = 'block';
            positionTooltip(field);
            tooltip.style.display = 'block';
            tooltip.style.opacity = '1';
          }
        });
      };
      field.addEventListener('focus', onFocus);
      // okta can focus the field before we get here (e.g. when re-rendering for a locale change), in which case
      // focusing it again from setInitialFocus() wouldn't fire a focus event
      if (document.activeElement === field) {
        onFocus();
      }
      field.addEventListener('blur', () => {
        focused = false;
        if (focusedField === field) {
          focusedField = null;
        }
        // hide tooltip
        contents.style.display = '';
        const tooltip = document.getElementById('infotooltip');
        tooltip.style.display = '';
        tooltip.style.opacity = '';
      });
    };

    // tooltip is in the body, not the scrollable right pane. this is so that the tooltip can overflow into
    // the left pane, but it means we have to manually reposition it whenever the right pane is scrolled. we
    // also need to reposition it whenever the field moves, like when displaying "this field is required",
    // hence the interval.
    const repositionTooltip = () => {
      if (focusedField) {
        positionTooltip(focusedField);
      }
    };
    this.addContainerScrollListener(repositionTooltip);
    this.intervals.push(setInterval(repositionTooltip, 100));

    const oldfield = expired ? this.elements.getOldPasswordField() : null;
    const newfield = this.elements.getNewPasswordField();
    if (newfield) {
      setFocusAndBlurHandlers(newfield, document.getElementById('newPasswordTooltip'));
      this.addValidIndicator(newfield); // green checkbox to right of field

      // performs a test and marks the rule good (green check) or bad (red x)
      const setRuleIndicator = (rule: string, test: () => boolean) => {
        const good = test();
        document.querySelector<HTMLElement>('#pwreq-' + rule + ' > svg:first-of-type').style.display = good ? '' : 'none';
        document.querySelector<HTMLElement>('#pwreq-' + rule + ' > svg:last-of-type').style.display = good ? 'none' : '';
        return good;
      };

      const newPasswordValidator = () => {
        let result = setRuleIndicator('length', () => {
          return newfield.value.length >= 10;
        });
        result = setRuleIndicator('lower', () => {
          return !!(/[a-z]/).test(newfield.value);
        }) && result;
        result = setRuleIndicator('upper', () => {
          return !!(/[A-Z]/).test(newfield.value);
        }) && result;
        result = setRuleIndicator('digit', () => {
          return !!(/[0-9]/).test(newfield.value);
        }) && result;
        result = setRuleIndicator('symbol', () => {
          return !!(/["!#$%&’()*+,\-./\\:;<=>?@[\]^_`{|}~]/).test(newfield.value);
        }) && result;
        result = setRuleIndicator('username', () => {
          // validate not using a part of the user name as per https://support.okta.com/help/s/question/0D51Y000069RzZI/password-policy-does-not-contain-part-of-username?language=en_US
          // split email address on symbols, and valid email symbols were derived from this page: https://social.technet.microsoft.com/Forums/exchange/en-US/69f393aa-d555-4f8f-bb16-c636a129fc25/what-are-valid-and-invalid-email-address-characters
          const parts = (iamLogin.page as WidgetPage).getUsername().split(/[!#$%&'*+\-/=?^_`{|}~. "(),:;<>@[\\\]]/);
          return newfield.value.length > 0 && !parts.some(part => {
            return part.length > 3 && newfield.value.indexOf(part) >= 0;
          });
        }) && result;
        if (expired) {
          result = setRuleIndicator('oldpw', () => {
            return newfield.value.length > 0 && newfield.value !== oldfield.value;
          }) && result;
        }
        // if all tests pass, show the green check to the right of the field
        this.showValidIndicator(newfield, result);
      };
      if (expired) {
        oldfield.addEventListener('input', newPasswordValidator); // on change password page, old password input can influence status of the new password (new password invalid if it equals the old password)
      }
      newfield.addEventListener('input', newPasswordValidator);
      newPasswordValidator();

      const repeatfield = this.elements.getConfirmPasswordField();
      if (repeatfield) {
        setFocusAndBlurHandlers(repeatfield, document.getElementById('repeatPasswordTooltip'));
        this.addValidIndicator(repeatfield); // green checkbox to right of field

        const repeatPasswordValidator = () => {
          const result = setRuleIndicator('match', () => {
            return repeatfield.value.length > 0 && repeatfield.value === newfield.value;
          });
          // if all tests pass, show the green check to the right of the field
          this.showValidIndicator(repeatfield, result);
        };
        newfield.addEventListener('input', repeatPasswordValidator);
        repeatfield.addEventListener('input', repeatPasswordValidator);
        repeatPasswordValidator();
      }
    }
    iamLogin.page.initializeCommon();
  }

  private isPasswordExpiredWidget() {
    return this.contextName === 'password-expired';
  }

  public addValidIndicator(field: HTMLInputElement) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttributeNS(null, 'id', field.name + '_status');
    svg.setAttributeNS(null, 'class', 'lsg-icon-component status-icon status-good');
    svg.style.display = 'none';
    const svgUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    svgUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', 'images/lsg-sprites.svg#allow-outlined');
    svg.appendChild(svgUse);
    const positionIt = function () {
      const pos = iamUtils.getRelativePosition(field, document.body);
      svg.style.left = (pos.left + pos.width + 15) + 'px';
      svg.style.top = (pos.top + pos.height / 2 - 10.5) + 'px';
    };
    document.body.appendChild(svg);
    this.statusIcons.push(svg);
    // handling indicator icons similarly to the tooltip in terms of positioning with scroll handler and interval.
    positionIt();
    this.addContainerScrollListener(positionIt);
    this.intervals.push(setInterval(positionIt, 100));
  };

  private addContainerScrollListener(listener: () => void) {
    document.getElementById('right-pane-contents').addEventListener('scroll', listener);
    this.containerScrollListeners.push(listener);
  }

  public override deinitialize() {
    // the scrollable container, tooltip and status icons outlive the widget's view, so undo what was set up on them
    this.intervals.forEach(interval => clearInterval(interval));
    const container = document.getElementById('right-pane-contents');
    this.containerScrollListeners.forEach(listener => container?.removeEventListener('scroll', listener));
    this.statusIcons.forEach(icon => icon.remove());
    this.intervals = [];
    this.containerScrollListeners = [];
    this.statusIcons = [];
  }

  public showValidIndicator(field: HTMLInputElement, show: boolean) {
    document.getElementById(field.name + '_status').style.display = show ? '' : 'none';
  };

  /**
   * When the Okta widget transitions between one view and another (e.g. password to mfa), it "slides" the new view in
   * and has 2 forms, the old and the new, until the transition is complete. This function waits until the transition
   * completes to run the given code so that DOM/positioning has settled. And if it's not transitioning, it'll run the
   * code immediately.
   *
   * @param fn function to run when not transitioning
   */
  public runWhenNotContextTransitioning(fn: () => void) {
    if (document.forms.length === 1) {
      fn();
    } else {
      const wait = setInterval(() => {
        if (document.forms.length === 1) { // transition between contexts when there are multiple forms
          clearInterval(wait);
          fn();
        }
      }, 50);
      // if the screen is left mid-transition, stop waiting rather than run fn against the next screen
      this.intervals.push(wait);
    }
  }
}
